/**
 * ORDENAÇÃO, RECORTE e RESUMO da lista de `/estoque`.
 *
 * Vive fora da página pelo mesmo motivo do `quoteListFilters`/`catalogListFilters`
 * (#193/#196): uma lista ordenada ou recortada errado **não dá erro, só esconde
 * mercadoria** — e aqui o que ela esconde é justamente o que está acabando.
 *
 * ─── O QUE ESTE MÓDULO EXISTE PARA NÃO DEIXAR QUEBRAR ───────────────────────
 *
 * · **A SITUAÇÃO sai de `alertKindOf`, nunca de uma conta local.** A escada
 *   negativo → zerado → baixo é a fonte única do app (`stockReport.ts`), e é ela
 *   que a planilha e o relatório já usam. Uma segunda implementação aqui faria a
 *   tela e o relatório discordarem sobre o mesmo produto na primeira correção de
 *   um dos dois (#82/#90).
 *
 * · **Os baldes são EXCLUSIVOS, e é por isso que as contagens fecham** (#52). Um
 *   produto zerado COM mínimo definido aparece uma vez só, como "Zerado" (o mais
 *   grave). Somar `isLowStock` (que se sobrepõe de propósito) com os outros dois
 *   faria "Com alerta" prometer mais linhas do que a lista entrega — e o número
 *   que não bate depois do clique é pior do que número nenhum (#191).
 *
 * · **`min_stock` nulo é "sem alerta", não "mínimo zero"** (#61): `Number(null)`
 *   é 0, e um produto sem mínimo passaria a alertar ao zerar por um caminho que
 *   ninguém escolheu. Quem trata isso é `isLowStock`; nada aqui converte.
 *
 * · **A busca dobra ACENTO** (#190d). O catálogo é escrito à mão em pt-BR e um
 *   `toLowerCase().includes()` cru devolve ZERO para quem digita "eletrica" no
 *   teclado do celular — e a tela responde "nenhum produto encontrado" para um
 *   produto que existe.
 *
 * · **Ordem instável** (#93): todo comparador desempata por `id`. Sem isso duas
 *   variações do mesmo produto (que têm o MESMO `products.name`, #30d) trocam de
 *   lugar entre renders e a lista "pisca" sem ninguém ter mexido em nada.
 */
import { normalizeSearchText } from '@/lib/quoteSearch';
import { alertKindOf, balanceOf, stockUnitLabel, type StockUnitRow } from '@/lib/stockReport';

/** O que a lista lê de uma unidade estocável (o cache devolve linhas parciais). */
export type StockListRow = StockUnitRow & {
  barcode?: string | null;
  _offline_pending?: boolean;
};

// ─── Busca ───────────────────────────────────────────────────────────────────

/**
 * Termo vazio casa com tudo.
 *
 * `parentName` vem do índice do `useStock` e é obrigatório na busca porque a
 * VARIAÇÃO é uma linha própria: sem ele, procurar "Camiseta" não acharia o
 * tamanho P (cujo rótulo é só "P"), e a tela pareceria dizer que o produto não
 * tem estoque nenhum.
 */
export function stockUnitMatchesSearch(
  unit: StockListRow,
  parentName: string | null | undefined,
  term: string,
): boolean {
  const needle = normalizeSearchText(term);
  if (!needle) return true;
  return [unit.name, unit.variant_name, unit.category, unit.barcode, parentName].some(
    (value) => normalizeSearchText(value).includes(needle),
  );
}

// ─── Recorte por SITUAÇÃO ───────────────────────────────────────────────────

export type StockSituationFilter = 'all' | 'alert' | 'low' | 'zero' | 'negative';

/**
 * Os botões da faixa, na ordem em que se olha para eles.
 *
 * "Com alerta" é a UNIÃO dos três seguintes, e existe porque a pergunta mais
 * comum da tela é "o que precisa de atenção?" — sem ele, respondê-la custaria
 * três cliques e uma soma de cabeça. Os outros três continuam separados porque
 * a urgência deles é diferente: negativo é dívida de saldo, zerado impede
 * vender, baixo é aviso.
 *
 * MEDIDO pela régua do #191: os cinco rótulos somam 34 caracteres contra os 43
 * dos cinco de `/quotes`, que couberam em 328px com `text-[11px]` + `px-1.5`.
 */
export const STOCK_SITUATION_FILTERS: readonly {
  value: StockSituationFilter;
  label: string;
}[] = [
  { value: 'all', label: 'Todos' },
  { value: 'alert', label: 'Com alerta' },
  { value: 'low', label: 'Baixo' },
  { value: 'zero', label: 'Zerado' },
  { value: 'negative', label: 'Negativo' },
];

export function matchesStockSituation(
  unit: StockListRow,
  filter: StockSituationFilter,
): boolean {
  if (filter === 'all') return true;
  const kind = alertKindOf(unit);
  if (filter === 'alert') return kind !== null;
  return kind === filter;
}

/**
 * Quantos cada botão traria. Sai do recorte SEM a situação (busca e categoria já
 * aplicadas) — contar sobre a lista inteira faria "Baixo 12" com uma busca ativa
 * que devolve 3, e contar depois zeraria todos os outros botões (#193).
 */
export function countStockUnitsBySituation(
  units: readonly StockListRow[],
): Record<StockSituationFilter, number> {
  const counts: Record<StockSituationFilter, number> = {
    all: units.length,
    alert: 0,
    low: 0,
    zero: 0,
    negative: 0,
  };
  for (const unit of units) {
    const kind = alertKindOf(unit);
    if (!kind) continue;
    counts.alert += 1;
    counts[kind] += 1;
  }
  return counts;
}

// ─── Recorte por CATEGORIA ──────────────────────────────────────────────────

/** Pseudo-valor do grupo do modal: "qualquer categoria". */
export const ALL_CATEGORIES = '';

/** As categorias presentes na lista, sem repetição e em ordem pt-BR. */
export function stockCategories(units: readonly StockListRow[]): string[] {
  const seen = new Set<string>();
  for (const unit of units) {
    const category = String(unit.category ?? '').trim();
    if (category) seen.add(category);
  }
  return Array.from(seen).sort((a, b) => a.localeCompare(b, 'pt-BR'));
}

export function matchesStockCategory(unit: StockListRow, category: string): boolean {
  if (!category) return true;
  return String(unit.category ?? '').trim() === category;
}

/**
 * Quantos filtros do MODAL estão ativos — vira o selo no ícone.
 *
 * A SITUAÇÃO fica de fora: ela tem os botões visíveis logo acima, e contar um
 * filtro que a tela já mostra faria o selo dizer "1" com o modal em branco
 * (#196).
 */
export function countActiveStockFilters(category: string): number {
  return category ? 1 : 0;
}

// ─── Ordenação ───────────────────────────────────────────────────────────────

export type StockSortOption = 'alert' | 'name' | 'nameDesc' | 'lowest' | 'highest';

export const STOCK_SORT_LABELS: Record<StockSortOption, string> = {
  alert: 'Alertas primeiro',
  name: 'Nome (A–Z)',
  nameDesc: 'Nome (Z–A)',
  lowest: 'Menor saldo',
  highest: 'Maior saldo',
};

export const STOCK_SORT_ORDER: readonly StockSortOption[] = [
  'alert',
  'name',
  'nameDesc',
  'lowest',
  'highest',
];

/**
 * ALERTAS PRIMEIRO é o padrão porque é o comportamento que a tela SEMPRE teve
 * (`isLowStock ? 0 : 1`, depois alfabético) — o padrão de uma configuração nova
 * tem que reproduzir o passado (#91). O que mudou é que a escada agora é a do
 * `alertKindOf`: negativo vem antes de zerado, que vem antes de baixo. Antes os
 * três empatavam (ou o negativo/zerado sem mínimo nem subia), e o produto mais
 * grave podia ficar abaixo de um aviso.
 */
export const DEFAULT_STOCK_SORT: StockSortOption = 'alert';

/** Peso da urgência: menor = mais grave = mais acima. */
function severityOf(unit: StockListRow): number {
  const kind = alertKindOf(unit);
  if (kind === 'negative') return 0;
  if (kind === 'zero') return 1;
  if (kind === 'low') return 2;
  return 3;
}

/**
 * `parentNameOf` resolve o nome do PAI da variação: sem ele a comparação
 * alfabética usaria `products.name`, que é IGUAL nas três variações (#30d), e a
 * ordem entre P/M/G ficaria por conta de quem devolveu as linhas.
 */
export function sortStockList<T extends StockListRow>(
  units: readonly T[],
  option: StockSortOption,
  parentNameOf: (unit: T) => string | null,
): T[] {
  const rows = [...units];
  const labelOf = (unit: T) => normalizeSearchText(stockUnitLabel(unit, parentNameOf(unit)));
  const byId = (a: T, b: T) => String(a.id).localeCompare(String(b.id));
  const byLabel = (a: T, b: T) => labelOf(a).localeCompare(labelOf(b), 'pt-BR') || byId(a, b);

  switch (option) {
    case 'name':
      return rows.sort(byLabel);
    case 'nameDesc':
      return rows.sort((a, b) => labelOf(b).localeCompare(labelOf(a), 'pt-BR') || byId(a, b));
    case 'lowest':
      return rows.sort((a, b) => balanceOf(a) - balanceOf(b) || byLabel(a, b));
    case 'highest':
      return rows.sort((a, b) => balanceOf(b) - balanceOf(a) || byLabel(a, b));
    case 'alert':
    default:
      return rows.sort((a, b) => severityOf(a) - severityOf(b) || byLabel(a, b));
  }
}

// ─── Resumo do hero ─────────────────────────────────────────────────────────

export interface StockListSummary {
  /** Unidades estocáveis no recorte (SKUs) — un e kg não se somam num total. */
  count: number;
  /** Quantas têm QUALQUER alerta — o número acionável da tela. */
  alertCount: number;
  negativeCount: number;
  zeroCount: number;
  lowCount: number;
}

export function summarizeStockList(units: readonly StockListRow[]): StockListSummary {
  const counts = countStockUnitsBySituation(units);
  return {
    count: units.length,
    alertCount: counts.alert,
    negativeCount: counts.negative,
    zeroCount: counts.zero,
    lowCount: counts.low,
  };
}

/**
 * O RÓTULO do número do hero.
 *
 * Mesmo raciocínio do `catalogListTotalLabel` (#195): numa lista sem dinheiro o
 * valor ao vivo é a CONTAGEM, e sem dizer de onde ela saiu o "12" lê como "é
 * tudo o que eu tenho em estoque" quando pode ser o resto de uma busca.
 */
export function stockListTotalLabel(filtered: boolean): string {
  return filtered ? 'Produtos no filtro' : 'Produtos com estoque';
}
